import Cookies from "js-cookie";
import React, { useState, useMemo, createContext, useEffect } from "react";
import _ from "lodash";
import posthog from "posthog-js";

// create context
export const CartContext = createContext();

export const CustomCartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [itemsIsUpdate, setItemsIsUpdate] = useState(false);
  // Update PostHog person properties when cart changes
  useEffect(() => {
    if (items.length > 0) {
      // Filter out unwanted fields from each cart item
      const filteredItems = items.map(item => {
        const { description, highlights, flashSale, ...filteredItem } = item;
        return filteredItem;
      });

      posthog.setPersonProperties({
        cart: filteredItems,
        cart_total: items.reduce((sum, item) => sum + item.itemTotal, 0),
        cart_items_count: items.length,
      });
    } else {
      posthog.setPersonProperties({
        cart: [],
        cart_total: 0,
        cart_items_count: 0,
      });
    }
  }, [items]);

  function addItem(item, newItem, addOn) {
    // Check if the item already exists in the cart
    const existingItem = items.find((i) => i._id === item._id);
    if (existingItem) {
      // If it does, increase the quantity
      setItems(
        items.map((i) => {
          if (i._id === item._id) {
            if (i.quantity + newItem <= i.oldQty) {
              return {
                ...i,
                quantity: i.quantity + newItem,
                itemPrice: i.price * newItem,
                itemSubTotal: i.originalPrice * (i.quantity + newItem),
                itemTotal: i.price * (i.quantity + newItem),
                inStock: true,
              };
            } else {
              return {
                ...i,
                quantity: i.quantity + newItem,
                itemPrice: i.price * newItem,
                itemSubTotal: i.originalPrice * (i.quantity + newItem),
                itemTotal: i.price * (i.quantity + newItem),
                inStock: false,
              };
            }
          }
          return i;
        })
      );
    } else {
      if (newItem <= item.quantity) {
        setItems([
          ...items,
          {
            ...item,
            quantity: newItem,
            addOnPrice: item.addOnPrice * newItem,
            itemPrice: item.price * newItem,
            itemSubTotal:
              item.originalPrice * newItem + item.addOnPrice * newItem,
            itemTotal: item.price * newItem + item.addOnPrice * newItem,
            inStock: true,
          },
        ]);
      } else {
        setItems([
          ...items,
          {
            ...item,
            quantity: newItem,
            addOnPrice: item.addOnPrice * newItem,
            itemPrice: item.price * newItem,
            itemSubTotal:
              item.originalPrice * newItem + item.addOnPrice * newItem,
            itemTotal: item.price * newItem + item.addOnPrice * newItem,
            inStock: false,
          },
        ]);
      }
    }
  }

  useEffect(() => {
    const cookie = window.localStorage.getItem("shoppingCart");

    if (JSON.parse(cookie)) {
      setItems(JSON.parse(cookie).items);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(
      "shoppingCart",
      JSON.stringify({
        items,
      })
    );
  }, [items, itemsIsUpdate]);

  const updateItemQuantity = (addOnPrice, id, delta, status) => {
    if (delta > 0)
      setItems(
        items.map((i) => {
          if (i._id === id) {
            if (delta <= i.oldQty) {
              return {
                ...i,
                quantity: delta,
                addOnPrice: i.baseAddonPrice * delta,
                itemPrice: i.price * delta,
                itemSubTotal:
                  i.originalPrice * delta + i.baseAddonPrice * delta,
                itemTotal: i.price * delta + i.baseAddonPrice * delta,
                inStock: true,
              };
            } else {
              return {
                ...i,
                quantity: delta,
                addOnPrice: i.baseAddonPrice * delta,
                itemPrice: i.price * delta,
                itemSubTotal:
                  i.originalPrice * delta + i.baseAddonPrice * delta,
                itemTotal: i.price * delta + i.baseAddonPrice * delta,
                inStock: false,
              };
            }
          }
          return i;
        })
      );
  };

  function removeItem(id) {
    setItems(items.filter((i) => i.id !== id));
  }

  const emptyCart = () => {
    setItemsIsUpdate(!itemsIsUpdate);

    setItems([]);
    window.localStorage.removeItem("shoppingCart");
  };

  const value = useMemo(() => {
    let isEmpty = items.length > 0 ? false : true;
    let cartTotal = items.reduce(
      (partialSum, item) => partialSum + item.itemTotal,
      0
    );

    const subTotal = items.reduce(
      (partialSum, item) => partialSum + item.itemSubTotal,
      0
    );

    let totalItems = items.reduce(
      (partialSum, item) => partialSum + item.quantity,
      0
    );

    let itemsDiscount = subTotal - cartTotal;

    const inStockItems = items.filter((i) => i.inStock);

    const preOrderItems = items.filter((i) => !i.inStock);

    return {
      items,
      addItem,
      updateItemQuantity,
      isEmpty,
      cartTotal,
      removeItem,
      totalItems,
      emptyCart,
      inStockItems,
      preOrderItems,
      subTotal,
      itemsDiscount,
    };
  }, [items, addItem, setItems, itemsIsUpdate]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
